import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Placeholder } from 'components/index.ts';
import styles from './OfferTable.module.scss';
import { format } from 'utils/format';
import { twMerge } from 'tailwind-merge';

type OfferTableProps = {
  totalPrincipal?: number;
  totalRepayableAmount?: number;
  extensionFees?: number;
  assignedInterest?: number;
  interestRate?: any;
  futureExtensionFees?: any;
  firstPaymentDate?: any;
  loading?: boolean;
};

// Empty while the rate is missing, so the row keeps its loading placeholder
const perAnnum = (rate: number | undefined) => {
  const percent = format.number(rate, 'percent');

  return percent && `${percent} p.a`;
};

const OfferTable = ({
  totalRepayableAmount,
  totalPrincipal,
  extensionFees,
  interestRate,
  assignedInterest,
  futureExtensionFees,
  firstPaymentDate,
  loading = false,
}: OfferTableProps) => {
  const { t } = useTranslation();

  const loanRows = [
    {
      label: 'offerTable:totalRepayableAmount',
      value: format.number(totalRepayableAmount, 'currency'),
      rowDataTest: 'offer-table-total-repayable-amount',
      valueDataTest: 'offer-table-total-repayable-amount-value',
    },
    {
      label: 'offerTable:interestRate',
      value: perAnnum(interestRate),
      rowDataTest: 'offer-table-interest-rate',
      valueDataTest: 'offer-table-interest-rate-value',
    },
    {
      label: 'offerTable:firstPaymentDate',
      value: format.dateTime(firstPaymentDate, 'date'),
      rowDataTest: 'offer-table-first-payment-date',
      valueDataTest: 'offer-table-first-payment-date-value',
    },
  ];

  const extensionRows = [
    {
      label: 'offerTable:extensionFees',
      value: format.number(extensionFees, 'currency'),
      rowDataTest: 'offer-table-extension-fees',
      valueDataTest: 'offer-table-extension-fees-value',
    },
    {
      label: 'offerTable:futureExtensionFees',
      value: format.number(futureExtensionFees, 'currency'),
      rowDataTest: 'offer-table-future-extension-fees',
      valueDataTest: 'offer-table-future-extension-fees-value',
    },
    {
      label: 'offerTable:interestRate',
      value: perAnnum(assignedInterest),
      rowDataTest: 'offer-table-assigned-interest',
      valueDataTest: 'offer-table-assigned-interest-value',
    },
    {
      label: 'offerTable:paymentDate',
      value: format.dateTime(firstPaymentDate, 'date'),
      rowDataTest: 'offer-table-payment-date',
      valueDataTest: 'offer-table-payment-date-value',
    },
  ];

  const renderRow = ({
    label,
    value,
    rowDataTest,
    valueDataTest,
  }: {
    label: string;
    value: ReactNode;
    rowDataTest: string;
    valueDataTest: string;
  }) => (
    <div key={rowDataTest} className={styles.row} data-test={rowDataTest}>
      <div>{t(label)}</div>
      <Placeholder
        className={styles.valueCell}
        loading={loading}
        dataTest={valueDataTest}
      >
        {value}
      </Placeholder>
    </div>
  );

  return (
    <div className={styles.offerTableWrapper} data-test="offer-table-wrapper">
      <div className={twMerge(styles.row, styles.title)}>
        <div data-test="offer-table-label">{t('offerTable:loan')}</div>
        <Placeholder
          className={styles.valueCell}
          loading={loading}
          dataTest="offer-table-total-principal"
        >
          {format.number(totalPrincipal, 'currency')}
        </Placeholder>
      </div>
      <Card className={styles.card} data-test="offer-table-card-loan">
        <div className={twMerge(styles.row, 'font-bold')}>
          <div>{t('offerTable:title_1')}</div>
        </div>
        {loanRows.map(renderRow)}
      </Card>

      <Card className={styles.card} data-test="offer-table-card-extension">
        <div className={twMerge(styles.row, 'font-bold')}>
          <div>{t('offerTable:title_2')}</div>
        </div>
        {extensionRows.map(renderRow)}
      </Card>
    </div>
  );
};

export default OfferTable;
